'use client';

import Link from 'next/link';
import { getSlugForCountry, uiFb } from '@/lib/i18n';
import type { CountryCode, CalendarEvent } from '@/lib/types';
import { COUNTRY_LIST } from '@/lib/constants';

import { setLangOverride } from '@/hooks/useAutoLang';
import { LANDING_IMAGES } from '@/lib/images';
import { FallbackImage } from '@/components/ui/FallbackImage';
import { LedgerHeroPeek } from './LedgerHeroPeek';
import { Flag } from '@/components/ui/Flag';

interface LandingHeroProps {
  entityIcon: string;
  entityLabel: string;
  year: number;
  langAlternates: Array<{ lang: string; slug: string; label: string }>;
  lang: string;
  events?: CalendarEvent[];
}

const TOP_DESTINATIONS = COUNTRY_LIST.slice(0, 6);

export function LandingHero({ entityIcon, entityLabel, year, langAlternates, lang, events }: LandingHeroProps) {
  const flagCodes = [...COUNTRY_LIST, ...COUNTRY_LIST].map(c => c.code);

  return (
    <section className="relative flex min-h-[60vh] flex-col items-center justify-center overflow-hidden px-4 pt-20 md:px-8 lg:px-16 xl:px-20">
      {/* Atlas background texture */}
      <FallbackImage
        aria-hidden="true"
        src={LANDING_IMAGES.heroAtlasBg}
        alt=""
        width={1920}
        height={1080}
        priority
        sizes="100vw"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-30"
      />

      {/* Decorative gradient orbs */}
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 top-1/4 h-64 w-64 rounded-full bg-brand/8 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 bottom-1/4 h-48 w-48 rounded-full bg-brand/6 blur-3xl" />

      {/* Flag parade background */}
      <div aria-hidden="true" className="pointer-events-none absolute top-12 left-0 right-0 overflow-hidden opacity-[0.07]">
        <div className="animate-flag-scroll flex items-center gap-2" style={{ width: 'max-content' }}>
          {flagCodes.map((code, i) => (
            <Flag key={`${code}-${i}`} code={code} size={36} />
          ))}
        </div>
      </div>

      <div className="relative mx-auto flex max-w-4xl flex-col items-center text-center">
        {/* Eyebrow — canonical brand tagline */}
        <p className="animate-fade-in-up delay-100 flex h-6 items-center text-sm font-medium uppercase tracking-widest text-muted">
          {uiFb('ui.brand_tagline', 'Every holiday, on display.', lang)}
        </p>

        {/* Headline — stable, no rotation */}
        <h1 className="animate-fade-in-up delay-100 mt-4 font-heading text-4xl font-black leading-[1.08] tracking-[-0.02em] text-text-primary md:text-6xl lg:text-7xl">
          <span className="block">{uiFb('ui.hero_line1', "Know what's open", lang)}</span>
          <span className="block">&mdash; {uiFb('ui.hero_line2', "and what's not", lang)} &mdash;</span>
          <span className="mt-1 block text-brand">
            {uiFb('ui.hero_scope', 'across 14 countries', lang)}
          </span>
        </h1>

        {/* Language switcher */}
        {langAlternates.length > 1 && (
          <div className="animate-fade-in-up delay-200 mt-5 flex flex-wrap items-center justify-center gap-1.5">
            {langAlternates.map((alt) => (
              <a
                key={alt.lang}
                href={`/${alt.slug}`}
                hrefLang={alt.lang}
                onClick={() => setLangOverride(alt.lang)}
                className={`inline-flex min-h-[44px] items-center rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                  alt.lang === lang
                    ? 'bg-brand text-text-on-brand shadow-sm'
                    : 'border border-border-subtle bg-surface-card text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                }`}
              >
                {alt.label}
              </a>
            ))}
          </div>
        )}

        {/* Subheadline */}
        <p className="animate-fade-in-up delay-400 mt-6 max-w-xl text-base leading-relaxed text-text-secondary md:text-lg">
          {uiFb('ui.hero_subheadline', 'See every public holiday, bank closure, and festival across 14 countries — always up-to-date, always free.', lang)}
        </p>

        {/* CTA */}
        <div className="animate-fade-in-up delay-550 mt-8 flex flex-col items-center gap-3 sm:flex-row">
          <Link
            href="/today"
            className="btn-brand-gradient inline-flex min-h-[44px] items-center gap-2 rounded-xl px-7 py-3 text-sm font-semibold shadow-md"
          >
            {uiFb('ui.cta_today', "What's happening today", lang)}
          </Link>
        </div>

        {/* Country flag quick-links */}
        <div className="animate-fade-in-up delay-600 mt-6 flex flex-col items-center gap-2">
          <span className="text-xs font-medium text-muted">{uiFb('ui.pick_country', 'Pick a country to start', lang)}</span>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {TOP_DESTINATIONS.map((dest) => {
              const slug = getSlugForCountry(dest.code as CountryCode, lang) || getSlugForCountry(dest.code as CountryCode, 'en');
              return (
                <a
                  key={dest.code}
                  href={`/${slug}`}
                  className="inline-flex min-h-[44px] items-center gap-1.5 content-card px-3 py-2 text-xs font-medium text-text-secondary transition-colors hover:border-brand/40 hover:bg-surface-hover hover:text-text-primary"
                >
                  <Flag code={dest.code} size={16} />
                  <span className="hidden sm:inline">{dest.label}</span>
                </a>
              );
            })}
            <a
              href="#countries"
              className="inline-flex min-h-[44px] items-center rounded-lg px-3 py-2 text-xs font-medium text-brand hover:underline"
            >
              + 8 {uiFb('ui.more_countries', 'more countries', lang)}
            </a>
          </div>
        </div>

        {/* Upcoming events peek */}
        {events && events.length > 0 && (
          <LedgerHeroPeek events={events} year={year} />
        )}
      </div>
    </section>
  );
}
